import { weights } from '@components/theme/styles/index';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons';
import {
  faPencil,
  faCopy,
  faChevronDown,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FeatureFlags } from '@gen2/app/components/feature-flags/feature-flags';
import { Box, IconButton, styled, TextField, Tooltip } from '@mui/material';
import { rem } from 'polished';
import React from 'react';

const StyledTitle = styled('span')<{ $isGroup?: boolean }>(({ $isGroup }) => ({
  fontWeight: weights.semiBold,
  fontSize: rem(16),
  letterSpacing: '0.5px',
  textTransform: $isGroup ? 'uppercase' : 'none',
  display: 'block',
  textAlign: 'left',
  wordBreak: 'break-word',
  whiteSpace: 'pre-line',
}));

type EditableTitleProps = {
  title: string;
  isEditing: boolean;
  onEdit: () => void;
  onDuplicateGroup: () => void;
  onExpandCollapseAll: (title: string) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  error?: string;
  helperText?: string;
  isGroup?: boolean;
  inputProps?: any;
  expandAllButtonState?: 'expand' | 'collapse';
};

// TODO: Feature flag for expand and collapse all group requests
export const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  isEditing,
  onEdit,
  onDuplicateGroup,
  onExpandCollapseAll,
  onChange,
  onSave,
  error,
  helperText,
  isGroup,
  inputProps,
  expandAllButtonState = 'expand',
}) => {
  return (
    <>
      {isGroup && isEditing ? (
        <TextField
          fullWidth
          value={title}
          onChange={onChange}
          onBlur={onSave}
          size="small"
          autoFocus
          variant="filled"
          sx={{ minWidth: 120, mr: 1 }}
          inputProps={inputProps}
          error={!!error}
          helperText={helperText}
        />
      ) : (
        <StyledTitle $isGroup={isGroup}>{title}</StyledTitle>
      )}
      {isGroup && !isEditing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Edit Group Name" placement="top">
            <IconButton
              color="tonalDark"
              size="small"
              onClick={onEdit}
              data-testid="edit-group-title-btn"
            >
              <FontAwesomeIcon size="xs" icon={faPencil} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate Group Requests" placement="top">
            <IconButton
              color="tonalDark"
              size="small"
              onClick={onDuplicateGroup}
              data-testid="copy-group-title-btn"
            >
              <FontAwesomeIcon size="xs" icon={faCopy} />
            </IconButton>
          </Tooltip>
          <FeatureFlags restriction="expand_and_collapse_feature">
            <Tooltip
              title={
                expandAllButtonState === 'collapse'
                  ? 'Collapse All Requests'
                  : 'Expand All Requests'
              }
              placement="top"
            >
              <IconButton
                color="tonalDark"
                size="small"
                onClick={() => onExpandCollapseAll(title)}
                data-testid="expand-collapse-all-group-requests-btn"
              >
                <FontAwesomeIcon
                  size="xs"
                  icon={
                    expandAllButtonState === 'collapse'
                      ? faChevronUp
                      : faChevronDown
                  }
                />
              </IconButton>
            </Tooltip>
          </FeatureFlags>
        </Box>
      )}
    </>
  );
};
